import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { IconEye } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useTableSort } from '../../lib/table-sort';
import { useOpdPatientBills } from './opd.queries';
import { currency, formatOpdDateTime } from './opd.utils';
import type { OpdRegistration } from './types';

interface MergedBillRow {
  id: string;
  billType: 'OPD';
  billNumber: string;
  date: string;
  doctorName: string;
  total: number;
  discount: number;
  grandTotal: number;
  paid: number;
  due: number;
  credit: number;
  status: 'due' | 'paid' | 'cancelled';
  link: string;
  isCurrent: boolean;
}

export const OpdAllBillList = ({ row }: { row: OpdRegistration }) => {
  const uhid = row.patientSnapshot.uhid;
  const opdQuery = useOpdPatientBills(uhid);

  const bills = useMemo<MergedBillRow[]>(() => {
    const opdBills: MergedBillRow[] = (opdQuery.data?.data ?? []).map((bill) => ({
      id: bill.id,
      billType: 'OPD',
      billNumber: bill.billing.billNumber,
      date: bill.appointmentDate,
      doctorName: doctorNameOrDash(bill.doctor.name),
      total: bill.billing.total,
      discount: bill.billing.totalDiscount,
      grandTotal: bill.billing.grandTotal,
      paid: bill.billing.totalPayment,
      due: bill.billing.dueAmount,
      credit: bill.billing.creditAmount,
      status: bill.billing.billStatus,
      link: `/opd/${bill.id}`,
      isCurrent: bill.id === row.id,
    }));
    return opdBills.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [opdQuery.data, row.id]);
  const tableSort = useTableSort(bills, {
    billNo: (bill) => bill.billNumber,
    date: (bill) => bill.date,
    doctor: (bill) => bill.doctorName,
    total: (bill) => bill.total,
    discount: (bill) => bill.discount,
    grandTotal: (bill) => bill.grandTotal,
    paid: (bill) => bill.paid,
    due: (bill) => bill.due,
    credit: (bill) => bill.credit,
    status: (bill) => bill.status,
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their billing history can't be looked up." />;
  if (opdQuery.isLoading) return <LoadingState />;
  if (opdQuery.isError) return <ErrorState title="Unable to load bills" message={opdQuery.error instanceof ApiError ? opdQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void opdQuery.refetch() }} />;

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>All Bill — {row.patientSnapshot.name}</span></div>
        <p>UHID {uhid} · {bills.length} bill{bills.length === 1 ? '' : 's'}</p>
      </header>
      <section className="opd-bill-subsection">
        {bills.length === 0 ? (
          <p className="opd-bill-empty-note">No bills were found for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billNo">Bill No</SortableTh><SortableTh control={tableSort} column="date">Date</SortableTh><SortableTh control={tableSort} column="doctor">Doctor</SortableTh><SortableTh control={tableSort} column="total">Total</SortableTh><SortableTh control={tableSort} column="discount">Discount</SortableTh><SortableTh control={tableSort} column="grandTotal">Grand Total</SortableTh><SortableTh control={tableSort} column="paid">Paid</SortableTh><SortableTh control={tableSort} column="due">Due</SortableTh><SortableTh control={tableSort} column="credit">Credit</SortableTh><SortableTh control={tableSort} column="status">Status</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {tableSort.rows.map((bill, index) => (
                  <tr key={`${bill.billType}-${bill.id}`} className={bill.isCurrent ? 'opd-current-row' : undefined}>
                    <td>{index + 1}</td>
                    <td>{bill.billNumber} <span className={`opd-badge ${bill.billType === 'OPD' ? 'patient-type' : 'rescheduled'}`}>{bill.billType}</span></td>
                    <td>{formatOpdDateTime(bill.date)}</td>
                    <td>{bill.doctorName}</td>
                    <td>{currency(bill.total)}</td>
                    <td>{currency(bill.discount)}</td>
                    <td>{currency(bill.grandTotal)}</td>
                    <td>{currency(bill.paid)}</td>
                    <td>{currency(bill.due)}</td>
                    <td>{currency(bill.credit)}</td>
                    <td><span className={`opd-badge ${bill.status}`}>{bill.status === 'due' ? 'Due' : bill.status === 'cancelled' ? 'Cancelled' : 'Paid'}</span></td>
                    <td>
                      {bill.isCurrent
                        ? <span className="opd-badge current">Current</span>
                        : <Link className="opd-row-action-icon is-plain" to={bill.link} title="View" aria-label={`View bill ${bill.billNumber}`}><IconEye /></Link>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
